import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { lazyModule } from '../../lib/lazyModule';
import { FolderKanban, Lock, AlertTriangle, Eye, Loader2, Calendar, GanttChart, Columns, Network } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCopy, fill } from '../../i18n';
import * as fmt from '../../i18n/format';
import { plural } from '../../i18n/format';
import { projectService, type SharedProjectPayload } from '../../services/projectService';
import { buildTaskTree } from '../../services/taskService';
import { computePublicScheduleStats } from '../../utils/publicScheduleStats';
import { createRateLimiter } from '../../utils/rateLimiter';
import { useVirtualKeyboard } from '../../hooks/useVirtualKeyboard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { SegmentedControl } from '@/components/ui/segmented-control';
import type { Task, KanbanColumn, TeamMember } from '../../types';
// Card/CardContent still needed for password screen

const SharedGanttView = lazyModule(() => import('./SharedGanttView'));
const SharedKanbanView = lazyModule(() => import('./SharedKanbanView'));
const SharedEapView = lazyModule(() => import('./SharedEapView'));

interface SharedProject {
  id: string;
  name: string;
  settings?: { publish?: { defaultView?: string; views?: string[] } };
  passwordRequired?: boolean;
  [key: string]: unknown;
}

type SharedView = 'gantt' | 'kanban' | 'eap';

interface SharedProjectViewProps {
  shareToken: string;
}

/** `key` é o valor persistido em settings.publish.defaultView; o rótulo vem do dicionário. */
const VIEW_CONFIG: { key: SharedView; icon: typeof GanttChart }[] = [
  { key: 'gantt', icon: GanttChart },
  { key: 'kanban', icon: Columns },
  { key: 'eap', icon: Network },
];

const TODAS_AS_LENTES = VIEW_CONFIG.map(v => v.key);

/**
 * Quais lentes este link expõe. Quem publica escolhe, e o padrão é todas.
 *
 * O princípio é o mesmo que tirou "Edição" do link: quem manda na informação é o
 * gerente de projetos, não a ferramenta. Cliente de obra recebe só o cronograma;
 * cliente de agência recebe também o quadro. Lista vazia ou ausente cai no
 * comportamento histórico (as três), para link já publicado não mudar sozinho.
 */
const lentesPublicadas = (proj: SharedProject | null): SharedView[] => {
  const escolhidas = proj?.settings?.publish?.views;
  if (!Array.isArray(escolhidas) || escolhidas.length === 0) return TODAS_AS_LENTES;
  const validas = escolhidas.filter((v): v is SharedView => TODAS_AS_LENTES.includes(v as SharedView));
  return validas.length > 0 ? validas : TODAS_AS_LENTES;
};

/**
 * A casca das quatro telas desta rota (carregando, erro, senha e cronograma).
 *
 * `dvh` e não `vh`: no celular a barra do navegador entra e sai, e `100vh` corta
 * o rodapé. E o desconto de `--altura-teclado` existe porque esta rota roda FORA
 * do shell autenticado do `App.tsx`, então precisa assinar o teclado por conta
 * própria (ver `useVirtualKeyboard()` no corpo do componente).
 */
const CASCA =
  'h-dvh max-md:h-[calc(100dvh_-_var(--altura-teclado,0px))] flex flex-col bg-[var(--color-bg-secondary)] overflow-hidden';

// Rate limiter: 5 tentativas de senha por minuto (complementa lockout server-side)
const passwordLimiter = createRateLimiter(5, 60000);

/** Data curta do cabeçalho (04/03/26, 3/4/26), no formato do idioma corrente. */
const shortDate = (d: Date) =>
  fmt.date(d, { day: '2-digit', month: '2-digit', year: '2-digit' });

const SharedProjectView = ({ shareToken }: SharedProjectViewProps) => {
  const t = useCopy().sharing.publicProject;
  const [project, setProject] = useState<SharedProject | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [kanbanColumns, setKanbanColumns] = useState<KanbanColumn[] | null>(null);
  const [activeView, setActiveView] = useState<SharedView>('gantt');

  // Esta rota roda fora do shell do App.tsx, então assina o teclado por conta
  // própria. Sem isto, `--altura-teclado` nunca é publicada aqui e o campo de
  // senha fica sem a rede que o resto do produto tem.
  useVirtualKeyboard();

  // O cronograma de um cliente não é conteúdo de site. `robots.txt` cobre o
  // rastreador que o respeita; a meta cobre quem chega pelo link direto e
  // indexaria assim mesmo.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  // Carregar projeto público por token (verificação server-side)
  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: payload, error: fetchError } = await projectService.getSharedProjectData(shareToken);

        if (fetchError) {
          setError(fetchError.message || t.notFound);
          setLoading(false);
          return;
        }

        if (payload?.passwordRequired) {
          setProject({ name: payload.name as string } as SharedProject);
          setNeedsPassword(true);
          setLoading(false);
          return;
        }

        if (!payload?.project) {
          setError(t.notFound);
          setLoading(false);
          return;
        }

        aplicarPayload(payload);
        setAuthenticated(true);
        setLoading(false);
      } catch (err: unknown) {
        setError((err instanceof Error ? err.message : null) || t.notFound);
        setLoading(false);
      }
    };

    loadProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  /**
   * Projeto, tarefas e colunas do quadro chegam juntos, de uma RPC só.
   *
   * Antes eram duas idas: o projeto por RPC (que funciona para anônimo) e as
   * tarefas por `select('*')` comum, que a RLS devolvia VAZIO para quem não tem
   * sessão. O dono do link nunca via o defeito, porque ele abre o próprio link
   * autenticado.
   */
  const aplicarPayload = (payload: SharedProjectPayload) => {
    const proj = payload.project as unknown as SharedProject;
    setProject(proj);
    setTasks(buildTaskTree((payload.tasks || []) as never));
    // Vazia a menos que quem publicou tenha ligado "Mostrar responsáveis". Não
    // há segunda ida ao banco: o servidor já decidiu se manda a lista.
    setTeamMembers(payload.team ?? []);
    setKanbanColumns(payload.kanbanColumns ?? null);

    const lentes = lentesPublicadas(proj);
    const padrao = proj.settings?.publish?.defaultView as SharedView | undefined;
    setActiveView(padrao && lentes.includes(padrao) ? padrao : lentes[0]);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (!passwordLimiter.isAllowed()) {
      setPasswordError(t.tooManyAttempts);
      return;
    }

    setLoading(true);
    try {
      const { data: payload, error: verifyError } = await projectService.getSharedProjectData(shareToken, password);

      if (verifyError) {
        setLoading(false);
        if (verifyError.message === 'wrong_password') {
          setPasswordError(t.wrongPassword);
        } else if (verifyError.message === 'too_many_attempts') {
          setPasswordError(t.lockedOut);
        } else {
          setPasswordError(verifyError.message);
        }
        return;
      }

      if (!payload?.project || payload.passwordRequired) {
        setLoading(false);
        setPasswordError(t.verifyFailed);
        return;
      }

      aplicarPayload(payload);
      setNeedsPassword(false);
      setAuthenticated(true);
    } catch {
      setPasswordError(t.loadFailed);
    }
    setLoading(false);
  };

  // A regra e o porquê moram em `utils/publicScheduleStats.ts`. Resumo: anel e
  // contador compartilham UMA base, porque até 28/08/2026 o anel era média das
  // raízes e o contador era folhas, e a tela do cliente dizia 89% num projeto
  // 56% concluído.
  const projectStats = useMemo(() => computePublicScheduleStats(tasks), [tasks]);

  // --- Screens: Loading, Error, Password ---

  if (loading) {
    return (
      <div className={CASCA}>
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-[var(--color-text-secondary)]">
          <Loader2 size={40} className="animate-spin" />
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={CASCA}>
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center p-5 [&_svg]:text-[var(--color-warning)]">
          <AlertTriangle size={48} />
          <h2 className="text-[var(--color-text)] text-lg font-semibold m-0">{t.unavailableTitle}</h2>
          <p className="text-[var(--color-text-secondary)] text-sm max-w-[400px]">{error}</p>
          <a href="/" className="text-[var(--color-primary)] no-underline font-medium mt-2 hover:underline">{t.backHome}</a>
        </div>
      </div>
    );
  }

  if (needsPassword && !authenticated) {
    return (
      <div className={CASCA}>
        <div className="flex-1 flex items-center justify-center p-5 bg-[var(--color-bg-secondary)]">
          <Card className="w-full max-w-[400px] shadow-[var(--shadow-lg)]">
            <CardContent className="p-10 max-md:p-6">
              <div className="text-center mb-8 [&_svg]:text-[var(--color-primary)] [&_svg]:mb-4">
                <Lock size={32} />
                <h2 className="text-[var(--color-text)] text-lg font-semibold m-0 mb-2">{t.protectedTitle}</h2>
                <p className="text-[var(--color-text-secondary)] text-sm m-0">{t.protectedHint}</p>
              </div>
              <form onSubmit={handlePasswordSubmit}>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.passwordPlaceholder}
                    autoFocus
                    className="flex-1 py-3 px-4 border-2 border-[var(--color-border)] rounded-[var(--radius-lg)] text-base h-auto transition-colors duration-200 focus:border-[var(--color-primary)]"
                  />
                  <Button variant="primary" size="xl" type="submit" className="rounded-[var(--radius-lg)]">{t.submit}</Button>
                </div>
                {passwordError && (
                  <p className="text-[var(--color-danger-dark)] text-sm text-center mt-3">{passwordError}</p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const lentes = lentesPublicadas(project);
  const opcoesDeLente = VIEW_CONFIG.filter(v => lentes.includes(v.key)).map(({ key, icon }) => ({
    value: key,
    icon,
    // O rótulo some no telefone: o ícone basta, e o alvo continua com 44px.
    label: <span className="max-md:hidden">{t.views[key]}</span>,
  }));

  // --- Main authenticated view ---
  return (
    <div className={CASCA}>
      {/* Header + Tabs unified */}
      <header className="bg-[var(--color-bg)] border-b border-[var(--color-border)] shadow-[0_1px_3px_rgba(0,0,0,0.05)] shrink-0">
        <div className="flex items-center justify-between gap-3 py-3 px-6 max-md:px-4">
          {/* Left: project info */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-[var(--radius-lg)] bg-[var(--color-primary-bg)] flex items-center justify-center shrink-0">
              <FolderKanban size={18} className="text-[var(--color-primary)]" />
            </div>
            <div className="min-w-0">
              {/* O nome do projeto é a primeira coisa que o visitante precisa
                  reconhecer. Em 14px ele empatava com o metadado logo abaixo. */}
              <h1 className="text-[15px] font-semibold text-[var(--color-text)] m-0 truncate">{project?.name || t.untitled}</h1>
              <div className="flex items-center gap-3 text-[11px] text-[var(--color-text-muted)] mt-0.5">
                <span className="flex items-center gap-1">
                  <Calendar size={11} />
                  {projectStats.startDate ? shortDate(projectStats.startDate) : '-'} → {projectStats.endDate ? shortDate(projectStats.endDate) : '-'}
                </span>
                <span className="max-md:hidden">
                  {fill(plural(projectStats.taskCount, t.taskCount), {
                    done: projectStats.completedCount,
                    total: projectStats.taskCount,
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Center: lentes que este link expõe */}
          {opcoesDeLente.length > 1 && (
            <SegmentedControl
              options={opcoesDeLente}
              value={activeView}
              onChange={setActiveView}
              size="touch"
              className="shrink-0"
            />
          )}

          {/* Right: progress + badge */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="relative flex items-center justify-center">
              <svg width="36" height="36" viewBox="0 0 36 36" className="block">
                <circle cx="18" cy="18" r="15" fill="none" stroke="var(--color-border)" strokeWidth="3" />
                <circle cx="18" cy="18" r="15" fill="none" stroke="var(--color-primary)" strokeWidth="3"
                  strokeDasharray={`${projectStats.progress * 0.942} 94.2`} strokeLinecap="round" transform="rotate(-90 18 18)" />
              </svg>
              <span className="absolute text-[11px] font-bold text-[var(--color-text)]">{projectStats.progress}%</span>
            </div>
            {/* Somente leitura é o único modo que existe: "Edição" saiu do link
                público em 28/08/2026, por governança da informação. */}
            <span className="flex items-center gap-1 py-1 px-2.5 bg-[var(--color-bg-tertiary)] rounded-full text-[11px] text-[var(--color-text-muted)] font-medium max-md:hidden">
              <Eye size={12} />
              {t.viewOnly}
            </span>
          </div>
        </div>
      </header>

      {/* Active View */}
      <main className="flex-1 min-h-0 overflow-hidden" data-testid="cronograma-publicado">
        <Suspense fallback={
          <div className="flex items-center justify-center h-full gap-3 text-[var(--color-text-secondary)]">
            <Loader2 size={24} className="animate-spin" />
            <span className="text-sm">{t.loadingView}</span>
          </div>
        }>
          {activeView === 'gantt' && <SharedGanttView tasks={tasks} team={teamMembers} project={project} />}
          {activeView === 'kanban' && <SharedKanbanView tasks={tasks} team={teamMembers} project={project} columns={kanbanColumns} />}
          {activeView === 'eap' && <SharedEapView tasks={tasks} team={teamMembers} project={project} />}
        </Suspense>
      </main>

      {/* Footer */}
      <footer className="py-3.5 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)] flex items-center justify-center shrink-0 max-md:py-2.5 max-md:px-4 pb-[env(safe-area-inset-bottom)]">
        <span className="text-[13px] text-[var(--color-text-muted)]">{t.sharedVia} <a href="https://meugantt.com.br" target="_blank" rel="noopener noreferrer" className="text-[var(--color-primary)] hover:underline font-medium">MeuGantt</a></span>
      </footer>
    </div>
  );
};

export default SharedProjectView;
